import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ShoppingCart, ArrowRight } from 'lucide-react';
import { useCartStore } from '../store/useCartStore';
import { listPublicProducts } from '../lib/api';
import { getLocalizedText } from '../cms/localization';
import { productPath } from '../utils/slug';
import type { SupportedLang } from '../cms/localization';
import type { Product } from '../../shared/types';
import { formatPrice } from '../lib/formatPrice';
import { usePaymentSettings } from '../lib/usePaymentSettings';
import ResponsiveImage from '../components/ResponsiveImage';

interface ProductFunnelSectionProps {
  content: Record<string, unknown>;
}

const ProductCard: React.FC<{
  product: Product;
  className?: string;
}> = ({ product, className = '' }) => {
  const { addItem } = useCartStore();
  const { settings: paymentSettings } = usePaymentSettings();
  const priceOptions = {
    currencySymbol: paymentSettings.currencySymbol,
    locale: paymentSettings.priceLocale,
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (product.stockQuantity <= 0) return;
    addItem({
      id: product.slug,
      name: product.nameZh || product.nameEn || product.slug,
      price: product.price,
      image: product.imageUrl || '',
      variant: '',
      quantity: 1,
    });
  };

  return (
    <div
      className={`group bg-white border border-brand-ink/5 hover:border-brand-gold/40 hover:shadow-xl transition-all flex flex-col ${className}`}
    >
      <Link to={productPath(product.slug)} className="block aspect-square overflow-hidden bg-brand-stone relative">
        <ResponsiveImage
          src={product.imageUrl || '/images/warm-stones.jpg'}
          sizes={product.imageSizes}
          sizesAttr="(max-width: 640px) 260px, (max-width: 1024px) 300px, 25vw"
          alt={product.nameZh || product.nameEn || product.slug}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <span className="absolute top-3 left-3 bg-brand-cinnabar/90 text-brand-paper text-[10px] font-serif px-2 py-1 tracking-widest">
          正宗
        </span>
      </Link>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-serif text-lg text-brand-ink mb-1 line-clamp-1">
          {product.nameZh || product.nameEn || product.slug}
        </h3>
        <p className="text-brand-ink/60 font-sans text-sm mb-4 line-clamp-2 flex-1">
          {product.subtitleZh || product.descriptionZh || ''}
        </p>
        <div className="flex items-center justify-between gap-3">
          <span className="text-brand-cinnabar font-serif text-xl">
            {formatPrice(product.price, priceOptions)}
          </span>
          {product.stockQuantity <= 0 ? (
            <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
              暫時缺貨
            </span>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleAddToCart}
                className="w-9 h-9 rounded-full bg-brand-ink text-brand-paper hover:bg-brand-cinnabar transition-colors shadow-md flex items-center justify-center"
                aria-label="加入購物車"
                title="加入購物車"
              >
                <ShoppingCart className="w-4 h-4" />
              </button>
              <Link
                to={productPath(product.slug)}
                className="px-4 py-2 rounded-full bg-brand-ink text-brand-paper text-xs font-sans tracking-widest hover:bg-brand-cinnabar transition-colors shadow-[0_4px_0_0_rgba(0,0,0,0.25)] hover:shadow-[0_6px_0_0_rgba(0,0,0,0.22)] hover:-translate-y-0.5 active:shadow-none active:translate-y-1"
              >
                查看
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ProductFunnelSection: React.FC<ProductFunnelSectionProps> = ({ content }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as SupportedLang;

  const eyebrow = getLocalizedText(content.eyebrow, lang, t('products.eyebrow'));
  const title = getLocalizedText(content.title, lang, t('products.title'));
  const subtitle = getLocalizedText(content.subtitle, lang, t('products.subtitle'));

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await listPublicProducts({ limit: 200 });
        if (!cancelled && res.success && res.data) {
          setProducts(res.data.filter((p) => p.isActive));
        }
      } catch (err) {
        console.warn('Failed to load products for funnel:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const displayProducts = useMemo(() => products, [products]);
  const needsMarquee = displayProducts.length > 3;

  return (
    <section id="products" className="relative py-12 md:py-16 bg-brand-paper overflow-hidden">
      <style>{`
        @keyframes funnel-marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .funnel-marquee-track {
          animation: funnel-marquee 40s linear infinite;
        }
        .funnel-marquee-track:hover {
          animation-play-state: paused;
        }
      `}</style>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 md:mb-12">
          {eyebrow && (
            <span className="text-brand-cinnabar font-sans tracking-[0.25em] text-xs uppercase block mb-4">
              {eyebrow}
            </span>
          )}
          <h2 className="text-3xl md:text-4xl font-serif text-brand-ink">{title}</h2>
          {subtitle && (
            <p className="mt-4 text-brand-ink/60 font-sans text-base md:text-lg max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
        </div>

        {loading ? (
          <div className="text-center py-16 text-brand-ink/50">載入產品中…</div>
        ) : displayProducts.length === 0 ? (
          <div className="text-center py-16 text-brand-ink/50">
            暫無產品，請到後台新增或匯入示範產品。
          </div>
        ) : needsMarquee ? (
          <div className="relative overflow-hidden -mx-4 px-4">
            <div className="funnel-marquee-track flex gap-6 w-max">
              {[...displayProducts, ...displayProducts].map((product, idx) => (
                <ProductCard
                  key={`${product.slug}-${idx}`}
                  product={product}
                  className="w-[260px] sm:w-[300px] flex-shrink-0"
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {displayProducts.map((product) => (
              <ProductCard key={product.slug} product={product} className="w-full" />
            ))}
          </div>
        )}

        <div className="text-center pt-10 md:pt-12">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 px-8 py-4 bg-brand-ink text-brand-paper text-sm font-sans tracking-widest hover:bg-brand-cinnabar transition-colors"
          >
            瀏覽全部御陽產品
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ProductFunnelSection;
